<script setup>
import { cloneDeep, isEmpty, sortBy } from 'lodash';
import {
  ref, inject, computed,
} from 'vue';
import apiMaestrosLomloe from '@/api/cifra/lomloe/maestros';
import SelectCifra from '@/components/inputs/SelectCifra.vue';
import ModalSimple from '@/components/ModalSimple.vue';
import Spinner from '@/components/Spinner.vue';

const $I18n = inject('I18n');

const showModal = ref(false);
const cursoActual = ref({});
const titulo = ref(null);
const mainForm = ref(null);
const asignaturas = ref(null);
const cargando = ref(false);
const asignaturaSeleccionada = ref(null);
const competencias = ref([]);

const emits = defineEmits(['anadir']);

const noPuedeAnadir = computed(() => isEmpty(competencias.value));

async function cargarAsignaturas() {
  cargando.value = true;
  try {
    const anadidas = cursoActual.value.asignaturas.filter(
      (asig) => !asig._destroy,
    ).map(
      (asig) => asig.asignatura.id,
    );

    const infoCursoData = (await apiMaestrosLomloe.infoCurso(cursoActual.value.curso.id)).data;
    asignaturas.value = infoCursoData.info_curso.asignaturas.filter(
      (asg) => !anadidas.includes(asg.id),
    );
  } finally {
    cargando.value = false;
  }
}

function show(curso, opciones = {}) {
  cursoActual.value = cloneDeep(curso);
  titulo.value = opciones.titulo || $I18n.t('Titulo_asignaturas');
  showModal.value = true;

  asignaturaSeleccionada.value = null;
  competencias.value = [];

  cargarAsignaturas();
}

function cerrarModal() {
  showModal.value = false;
}

defineExpose({ show, cerrarModal });

function byOrden(array) {
  if (isEmpty(array)) { return []; }

  return sortBy(array, (o) => o.orden);
}

function gestionarCriterioEvaluacion(criterioEvaluacion) {
  const nuevo = {
    criterio_evaluacion_definicion_id: criterioEvaluacion.id,
    criterio_evaluacion_definicion: criterioEvaluacion,
  };

  const existeCompetencia = competencias.value.find(
    // eslint-disable-next-line max-len
    (compe) => compe.competencia_especifica_definicion_id === nuevo.criterio_evaluacion_definicion.competencia_especifica_definicion_id,
  );

  if (existeCompetencia) {
    const indexCriterio = existeCompetencia.criterios.findIndex(
      (cr) => cr.criterio_evaluacion_definicion.id === nuevo.criterio_evaluacion_definicion.id,
    );
    if (indexCriterio > -1) {
      existeCompetencia.criterios.splice(indexCriterio, 1);

      if (isEmpty(existeCompetencia.criterios)) {
        competencias.value.splice(
          competencias.value.findIndex(
            // eslint-disable-next-line max-len
            (compe) => compe.competencia_especifica_definicion.id === existeCompetencia.competencia_especifica_definicion.id, 1,
          ),
        );
      }
    } else {
      existeCompetencia.criterios.push(nuevo);
    }
  } else {
    const competencia = asignaturaSeleccionada.value.competencia_especifica_definiciones.find(
      // eslint-disable-next-line max-len
      (ced) => ced.id === nuevo.criterio_evaluacion_definicion.competencia_especifica_definicion_id,
    );

    const nuevaCompetencia = {
      competencia_especifica_definicion_id: competencia.id,
      competencia_especifica_definicion: competencia,
      criterios: [nuevo],
    };

    competencias.value.push(nuevaCompetencia);
  }
}
</script>
<template>
  <ModalSimple
    v-model="showModal"
    :titulo="titulo"
  >
    <div ref="mainForm">
      <Spinner v-if="cargando" :label="$t('cargando_informacion')" />
      <Transition enter-active-class="animated fadeInRight" mode="out-in">
        <div v-if="!cargando" class="asignaturas-wrapper">
          <div v-if="!isEmpty(asignaturas)" class="m-t">
            <div class="form-group">
              <SelectCifra
                v-model="asignaturaSeleccionada"
                :opciones="asignaturas"
                :buscable="true"
                :multiple="false"
                campo-etiqueta="nombre"
                :marcador="$I18n.t('Asignatura')"
                campo-id="id"
              />
            </div>
            <template v-if="!isEmpty(asignaturaSeleccionada)">
              <div v-if="!isEmpty(asignaturaSeleccionada.competencia_especifica_definiciones)">
                <div class="m-t border-bottom">
                  <h5>{{ $t('lomloe_competencias_especificas') }}</h5>
                </div>
                <ul
                  v-for="competenciaEspecifica in byOrden(
                    asignaturaSeleccionada.competencia_especifica_definiciones
                  )"
                  :key="competenciaEspecifica.id"
                  class="m-t-xs"
                >
                  <li>
                    {{ competenciaEspecifica.codigo }} - {{ competenciaEspecifica.nombre }}
                  </li>
                  <ul class="list-unstyled">
                    <li
                      v-for="criterioEvaluacion in byOrden(
                        competenciaEspecifica.criterio_evaluacion_definiciones
                      )"
                      :key="criterioEvaluacion.id"
                      class="m-t p-l-md"
                    >
                      <div class="pretty_checkbox">
                        <div class="pretty p-icon">
                          <input
                            class="pretty_checkbox"
                            type="checkbox"
                            @click="gestionarCriterioEvaluacion(criterioEvaluacion)"
                          >
                          <div class="state p-cifra">
                            <i class="icon fa fa-check"></i>
                            <label class="pretty_checkbox control-label"></label>
                          </div>
                        </div>
                        {{ criterioEvaluacion.codigo }} - {{ criterioEvaluacion.nombre }}
                      </div>
                    </li>
                  </ul>
                </ul>
              </div>
              <div v-else class="m-t">
                {{ $t('lomloe_competencias_especificas_no_hay') }}
              </div>
            </template>
          </div>
          <div v-else class="m-t">
            {{ $t('No_asignaturas_curso') }}
          </div>
          <div class="form-actions m-t">
            <button
              class="btn btn-default"
              type="button"
              @click="cerrarModal"
              v-text="$t('Cancelar')"
            />
            <button
              type="button"
              class="btn btn-cifra"
              :disabled="noPuedeAnadir"
              @click="emits(
                'anadir',
                {
                  cursoId: cursoActual.curso.id,
                  asignatura: asignaturaSeleccionada,
                  competencias: competencias
                }
              )"
            >
              <i class="fa fa-plus"></i> {{ $t('Anadir') }}
            </button>
          </div>
        </div>
      </Transition>
    </div>
  </ModalSimple>
</template>
